import React, { useState } from 'react';
import { Typography, Checkbox, FormGroup, FormControlLabel, Box } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import { useTranslation } from 'services/localization/useTranslation';
import { DiscountCodeValidity } from '../types';
import ValidityDatePicker from './ValidityDatePicker';

interface Props {
	validity: DiscountCodeValidity;
	onValidityChange: (validity: DiscountCodeValidity) => void;
}

const DiscountValidityLimit = (props: Props) => {
	const { validity } = props;
	const { t } = useTranslation();
	const [hasBookingTimeLimit, setHasBookingTimeLimit] = useState<boolean>(
		!!validity.validFrom || !!validity.validTo,
	);
	const [hasBookingStartTimeLimit, setHasBookingStartTimeLimit] = useState<boolean>(
		!!validity.bookingStartDateFrom || !!validity.bookingStartDateTo,
	);

	const handleBookingTimeLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		if (checked) {
			setHasBookingTimeLimit(true);
		} else {
			props.onValidityChange({
				...validity,
				validFrom: null,
				validTo: null,
			});
			setHasBookingTimeLimit(false);
		}
	};

	const handleBookingStartTimeLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		if (checked) {
			setHasBookingStartTimeLimit(true);
		} else {
			props.onValidityChange({
				...validity,
				bookingStartDateFrom: null,
				bookingStartDateTo: null,
			});
			setHasBookingStartTimeLimit(false);
		}
	};

	return (
		<ContainerBox padding="normal">
			<Typography sx={{ mb: 3 }} variant="h6">
				{t('discount.activeDatesLimit.activeDates', 'Active dates')}
			</Typography>
			<FormGroup>
				<FormControlLabel
					control={
						<Checkbox
							color="primary"
							checked={hasBookingTimeLimit}
							onChange={handleBookingTimeLimitChange}
						/>
					}
					label={t(
						'discount.activeDatesLimit.bookingCreateDateLimit',
						'Valid only for bookings created within a given date range',
					)}
				/>
			</FormGroup>
			{hasBookingTimeLimit && (
				<Box my={2} ml={4}>
					<ValidityDatePicker
						dateRange={[validity.validFrom ?? null, validity.validTo ?? null]}
						onDateRangeChange={(dates) => {
							const [validFrom, validTo] = dates;
							props.onValidityChange({
								...validity,
								validFrom,
								validTo,
							});
						}}
					/>
				</Box>
			)}
			<FormGroup>
				<FormControlLabel
					control={
						<Checkbox
							color="primary"
							checked={hasBookingStartTimeLimit}
							onChange={handleBookingStartTimeLimitChange}
						/>
					}
					label={t(
						'discount.activeDatesLimit.bookingStartDateLimit',
						'Valid only for bookings starting within a given date range',
					)}
				/>
			</FormGroup>
			{hasBookingStartTimeLimit && (
				<Box my={2} ml={4}>
					<ValidityDatePicker
						dateRange={[validity.bookingStartDateFrom ?? null, validity.bookingStartDateTo ?? null]}
						onDateRangeChange={(dates) => {
							const [bookingStartDateFrom, bookingStartDateTo] = dates;
							props.onValidityChange({
								...validity,
								bookingStartDateFrom,
								bookingStartDateTo,
							});
						}}
					/>
				</Box>
			)}
		</ContainerBox>
	);
};

export default DiscountValidityLimit;
